import DropDown from "../Inputs/DropDown";
import {useRef, useContext, useEffect, useState, useMemo, useCallback} from "react";
import Boolean from "../Inputs/Boolean";
import TextField from "../Inputs/TextField";
import Error from "../../../Alerts/Error";
import Success from "../../../Alerts/Success";
import {authContext} from "../../../../context/auth";
import { useParams } from "react-router-dom";
import File from "../Inputs/File";
import { useUploadQaHolds } from "../../../../util/uploadQa";
import useUploadQaWarningsConfirm from "../UploadQaWarnings";
import {progressContext} from "../../../../context/progress";
import filterResponse from "../../../../filterResponse";
import useCanContinue from "../../../../memos/useCanContinue";
import {missingAnswers, missingAnswersHint} from "../../../../util/missingAnswers";
import Spinner from "../../../Spinner";
import Info from "../../../Alerts/Info";
import List from "../Inputs/List";
import RegexField from "../Inputs/RegexField";
import canDeliver from "../../../../util/canDeliver";
import {testUploadSpeeds} from "../../../../util/fastestUploadTarget";

const DRAFT_SAVE_DELAY = 1000; // ms of no answer changes before the draft is saved

export default function EntryCollection({hasGeneralInputs, job, enabled, enable, disable, goto}) {
	const [answers, setAnswers] = useState({
		booleans: [... job.entryInputs.booleans?.map(value => {
			return  {
				checked: false,
				...value
			}
		})],
		dropDowns: [...job.entryInputs.dropDowns?.map(value => {
			return {
				value: '',
				...value
			};
		})],
		dropDownsMultiple: [...job.entryInputs.dropDownsMultiple?.map(value => {
			return {
				value: [],
				...value
			};
		})],
		lists: [...job.entryInputs.lists?.map(value => {
			return {
				value: [],
				...value
			};
		})],
		textFields: [...job.entryInputs.textFields?.map(value => {
			return {
				value: '',
				...value
			};
		})],
		regexFields: [...job.entryInputs.regexFields?.map(value => {
			return {
				value: '',
				...value
			};
		})],
		files: [...job.entryInputs.files?.map(value => {
			return {
				key: '',
				...value
			};
		})]
	});

	const {getProgress, loading: progressLoading, progress} = useContext(progressContext);

    const {user} = useContext(authContext);
    const {id} = useParams();
    const [loading, setLoading] = useState(false);
    const [uploadsInFlight, setFileLoading] = useState(0); // count, so concurrent uploads each hold the gate
    const fileLoading = uploadsInFlight > 0;
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
	const [fetchLoading, setFetchLoading] = useState(true);

	const [instruction, setInstruction] = useState(null);
	const [otherDisabledReason, setOtherDisabledReason] = useState(false);
	const [uploadQaHeld, setUploadQaHold, uploadQaWarnings] = useUploadQaHolds(); // a file still being checked, or one a blocker metric failed
	const [confirmUploadQaWarnings, uploadQaWarningsDialog] = useUploadQaWarningsConfirm();

	const [entryId, setEntryId] = useState(null); // for when you make double requests finalizing/changing an entry already submitted
	const pendingDraft = useRef(null); // the draft save body still waiting out DRAFT_SAVE_DELAY

	const inputs = [...job.entryInputs.booleans?.map(value => {
		return {
			boolean: true,
			...value
		}
	}), ...job.entryInputs.textFields?.map(value => {
		return {
			textField: true,
			...value
		}
	}), ...job.entryInputs.regexFields?.map(value => {
		return {
			regexField: true,
			...value
		}
	}), ...job.entryInputs.dropDowns?.map(value => {
		return {
			dropDown: true,
			...value
		}
	}), ...job.entryInputs.dropDownsMultiple?.map(value => {
		return {
			dropDown: true,
			multiple: true,
			...value
		}
	}), ...job.entryInputs.lists?.map(value => {
		return {
			value: [],
			list: true,
			...value
		};
	}), ...job.entryInputs.files?.map(value => {
		return {
			file: true,
			...value
		}
	})].sort((a, b) => a.name.localeCompare(b.name));


	const canContinue = useCanContinue(answers);

	// Measured as the page opens, so the first file already knows which bucket to go to.
	useEffect(() => {
		if (enabled) {
			testUploadSpeeds(id);
		}
	}, [enabled, id]);

	useEffect(() => {
		const tab = document.getElementById("tab" + 2);
		let isMounted = true;


		if (enabled) {
			tab.addEventListener("shown.bs.tab", onShown);


			(async () => {
				await getLast(isMounted);
			})();
		}

		async function onShown() {
			await getLast(isMounted);
		}

		return () => {
			tab.removeEventListener("shown.bs.tab", onShown);
			isMounted = false;
		};
	}, [enabled, id]);

	// The draft is saved whole on every answers change. Waiting for a pause in typing keeps that
	// to one request instead of one per keystroke; Submit stays disabled meanwhile (loading), so
	// finalize never runs ahead of the last save.
	useEffect (() => {
		pendingDraft.current = null;

		if (enabled) {
			const abortController = new AbortController();
			let isMounted = true;
			let timer;

			if (!fetchLoading) {
				setSuccess(null);
				setError(null);
				setLoading(true);

				const body = JSON.stringify({
					crowdJobId: id,
					freelancerId: user.id,
					answers: answers,
					entryId
				});
				pendingDraft.current = body;

				timer = setTimeout(async () => {
					pendingDraft.current = null;

					try {
						const response = await fetch(process.env.REACT_APP_ENDPOINT + "/crowdEntries", {
							method: "POST",
							credentials: "include",
							signal: abortController.signal,
							body,
							headers: {
								"Content-Type": "application/json"
							}
						});
						await filterResponse(response);

						if (response.status === 409) {
							await getProgress();
							await getLast();
						}
						else if (response.status !== 200 && response.status !== 201) {
							isMounted && setError("Failed to submit due to server error.");
						}
						else {
							const {entry} = await response.json();
							isMounted && setEntryId(entry.id);
						}
					}
					catch (e) {
						console.error(e);
						if (e.name !== 'AbortError') {
							isMounted && setError("Failed to submit due to server error.");
						}
					}

					isMounted && setLoading(false);
				}, DRAFT_SAVE_DELAY);
			}

			return () => {
				clearTimeout(timer);
				abortController.abort();
				isMounted = false;
			}
		}
	}, [enabled, answers, fetchLoading, id, user.id, entryId]);

	// A draft still waiting out the delay is sent anyway when the tab closes or the page is left,
	// so the last characters typed are not lost. keepalive lets the request outlive the page.
	useEffect(() => {
		function flushDraft() {
			const body = pendingDraft.current;
			if (!body) {
				return;
			}
			pendingDraft.current = null;
			fetch(process.env.REACT_APP_ENDPOINT + "/crowdEntries", {
				method: "POST",
				credentials: "include",
				keepalive: true,
				body,
				headers: {
					"Content-Type": "application/json"
				}
			}).catch(e => console.error("Failed to save the entry draft on leave", e));
		}

		window.addEventListener("pagehide", flushDraft);
		return () => {
			window.removeEventListener("pagehide", flushDraft);
			flushDraft();
		};
	}, []);

	const deliverable = canDeliver(progress);

	useEffect(() => {
		if (deliverable && enabled) {
			enable(3);
		}
		else {
			disable(3);
		}
	}, [deliverable, enabled]);

	// While uploads run the button already says why it is waiting, and a file mid-upload has no key yet.
	const submitHint = useMemo(() => {
		if (!answers || fetchLoading || loading || fileLoading || progress.done >= progress.maxRows) {
			return null;
		}
		return missingAnswersHint(missingAnswers(answers));
	}, [answers, fetchLoading, loading, fileLoading, progress.done, progress.maxRows]);

	const submitDisabled = useMemo(() => {
		return loading || fileLoading || progress.done >= progress.maxRows || !canContinue || otherDisabledReason || uploadQaHeld;
	}, [loading, fileLoading, progress.done, progress.maxRows, canContinue, otherDisabledReason, uploadQaHeld]);

	async function getLast(isMounted = true) {
		try {
			setFetchLoading(true);

			const response = await fetch(process.env.REACT_APP_ENDPOINT + `/crowdEntries/last?crowdJobId=${id}&freelancerId=${user.id}`, {
				method: "GET",
				credentials: "include",
				headers: {
					"Content-Type": "application/json"
				}
			});
			await filterResponse(response);

			if (response.status === 200) {
				const {entry} = await response.json();

				if (entry && isMounted) {
					isMounted && setInstruction(entry.instruction);
					entry.answers && isMounted && setAnswers(entry.answers);
					isMounted && setEntryId(entry.id);
				}
			}
			else if (response.status !== 404) {
				isMounted && setError("Server error");
			}
		}
		catch (e) {
			console.error(e);
			isMounted && setError("Failed to reach server");
		}

		isMounted && setFetchLoading(false);
	}

	async function onSubmit(evt) {
		evt.preventDefault();
		if (!(await confirmUploadQaWarnings(uploadQaWarnings))) {
			return;
		}
		try {
			setSuccess(null);
			setError(null);
			setLoading(true);

            const errors = [];
            for (const boolean of answers.booleans) {
                if (!boolean.checked && boolean.requiredTrue) {
                    errors.push(`"${boolean.name}"`);
                }
            }

            if (errors.length) {
                setError(`Please check the following checkboxes before you continue: ${errors.join(", ")}`);
                return;
            }

			const response = await fetch(process.env.REACT_APP_ENDPOINT + "/crowdEntries/finalize", {
				method: "PUT",
				body: JSON.stringify({
					crowdJobId: id,
					freelancerId: user.id,
					entryId
				}),
				headers: {
					"Content-Type": "application/json"
				},
				credentials: "include"
			});
			await filterResponse(response);

			if (response.status === 204) {
				setSuccess("Successfully submitted");
				setEntryId(null);
				resetAnswers();
				await getProgress();
			}
			else if (response.status === 409) {
				// A file in the entry has already been collected — the server names it. A bare 409
				// is the other kind: this tab's draft is stale, and reloading it is the answer. A
				// closed job is filterResponse's, which has already taken the page down.
				const body = await response.json().catch(() => null);
				if (body?.error && !body.jobClosed) {
					setError(body.error);
				}
				else {
					await getProgress();
					await getLast();
				}
			}
			else {
				setError("Failed to submit due to server error");
			}
		}
		catch (e) {
			console.error(e);
			setError("Failed to reach server");
		}
		finally {
			setLoading(false);
		}
	}

	const resetAnswers = useCallback(() => {
		return setAnswers({
			booleans: [... job.entryInputs.booleans?.map(value => {
				return  {
					checked: false,
					...value
				}
			})],
			dropDowns: [...job.entryInputs.dropDowns?.map(value => {
				return {
					value: '',
					...value
				};
			})],
			dropDownsMultiple: [...job.entryInputs.dropDownsMultiple?.map(value => {
				return {
					value: [],
					...value
				};
			})],
			lists: [...job.entryInputs.lists?.map(value => {
				return {
					value: [],
					...value
				};
			})],
			textFields: [...job.entryInputs.textFields?.map(value => {
				return {
					value: '',
					...value
				};
			})],
			regexFields: [...job.entryInputs.regexFields?.map(value => {
				return {
					value: '',
					...value
				};
			})],
			files: [...job.entryInputs.files?.map(value => {
				return {
					key: '',
					...value
				};
			})]
		});
	}, [job.entryInputs]);

	return (
		<>
            {error && <Error message={error} />}
            {uploadQaWarningsDialog}
            {success && <Success message={success} />}
			<h4>Entry Collection | {progress.done} ({progress.minRows}-{progress.maxRows})</h4>
			{progress.done < progress.minRows &&
				<p className={"text-muted"}>
					You have {progress.done} of the {progress.minRows} {progress.minRows === 1 ? 'entry' : 'entries'} this job asks for.
					{deliverable && ' You can deliver what you collected so far, or keep going.'}
				</p>
			}
			<form onSubmit={onSubmit} id={"entryForm"} />

			<div className={"input-sections " + (progress.done >= progress.maxRows ? "position-relative opacity-25 flex-fill": "flex-fill")}  translate="no">
					{progress.done >= progress.maxRows &&
						<div className={"position-absolute m-auto h-100 w-100 d-flex justify-content-center align-items-center"}>
							<p>You already submitted the maximum amount of entries.</p>
						</div>
					}
					{instruction && <Info message={instruction} /> }
					{inputs.map(value => {
						if (value.dropDown) {
							return <DropDown formId={"entryForm"} dropDown={value} answers={answers} setAnswers={setAnswers} key={value.id}/>;
						}
						if (value.boolean) {
							return <Boolean formId={"entryForm"} boolean={value} answers={answers} setAnswers={setAnswers} key={value.id}/>
						}
						if (value.textField) {
							return <TextField formId={"entryForm"} textField={value} answers={answers} setAnswers={setAnswers} key={value.id} />;
						}
						if (value.regexField) {
							return <RegexField formId={"entryForm"} regexField={value} answers={answers} setAnswers={setAnswers} key={value.id} />;
						}
						if (value.list) {
							return <List formId={"entryForm"} list={value} answers={answers} setAnswers={setAnswers} key={value.id} />;
						}
						if (value.file) {
							return <File formId={"entryForm"} setOtherDisabledReason={setOtherDisabledReason} setUploadQaHold={setUploadQaHold} setFileLoading={setFileLoading} file={value} answers={answers} setAnswers={setAnswers} key={value.id} />;
						}
						return  null;
					})}
				</div>
				<div className={"mt-3 mb-2 d-flex align-items-start gap-3"}>
					{hasGeneralInputs && <button type={"button"} className={"btn btn-secondary"} style={{whiteSpace: "pre"}} onClick={evt => {
						goto(1);
					}}>&lt;- Previous</button>}
					<div className={"w-100"}>
						<button type={"submit"} form={"entryForm"} className={"btn btn-success w-100"} disabled={submitDisabled}>{(loading || fileLoading) ? <><Spinner small={true} /> {fileLoading ? `Uploading${uploadsInFlight > 1 ? ` ${uploadsInFlight} files` : ''}…` : ''}</> : 'Submit & Next'}</button>
						{submitHint && <small className={"d-block text-muted text-center mt-1"}>{submitHint}</small>}
					</div>
					{deliverable && <button type={"button"} className={"btn btn-secondary"} style={{whiteSpace: "pre"}} onClick={evt => {
						goto(3);
					}}>Go to delivery -&gt;</button>}
				</div>
		</>
	)
}
